<script setup lang="ts">
import type { Store } from '@/views/contests/Components/types/global'

import dayjs from 'dayjs'
import { Calendar as CalendarIcon, Plus } from 'lucide-vue-next'
import { parseDate, getLocalTimeZone } from '@internationalized/date'

import type { Audience } from '@/types/types'

import type { TiggerFilter, TriggerSettings, TriggerType } from '../types/marketingFlow'
import { getTimeRangeDate } from '../utils'
const { t } = useI18n()
const { getTriggerStepErrors } = useMarketingFlow()

const props = defineProps<{
  modelValue: TriggerSettings
  stores: Store[]
  audiences: Audience[]
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', modelValue: TriggerSettings): void
  (e: 'onChanges', change: { isCritical: boolean; field?: string; value?: any }): void
}>()

const triggerStepErrors = computed(() => {
  return getTriggerStepErrors(props.modelValue)
})

const hasDateError = computed(() => {
  return triggerStepErrors.value.some((error) => error.field === 'filters' && error.i18nKey === 'startDateAfterEndDate')
})

const hasTimeRangeError = computed(() => {
  return triggerStepErrors.value.some((error) => error.field === 'filters' && error.i18nKey === 'startTimeAfterEndTime')
})

const triggerType = ref<TriggerType>(null)
const selectedTriggerType = ref<TriggerType>(null)

const isFullDay = ref<boolean>(false)

const startDateTime = ref<Date | null>(null)
const startTime = ref<Date | null>(null)
const endTime = ref<Date | null>(null)

const isRepeated = ref<boolean>(false)
const selectedRepeat = ref<string | null>(null)

const finalEndDateTime = ref<Date | null>(null)
const finalEndTime = ref<Date | null>(null)

const repeatOptions = computed(() => {
  return [
    {
      label: t('pages.marketingFlow.marketingFlowEditPage.trigger.repeat_choice.daily'),
      value: 'DAILY'
    },
    {
      label: t('pages.marketingFlow.marketingFlowEditPage.trigger.repeat_choice.weekly', {
        day: dayjs(startDateTime.value).format('dddd')
      }),
      value: 'WEEKLY'
    }
  ]
})

const repeatFilters = computed<TiggerFilter[]>(() => {
  if (!isRepeated.value) {
    return []
  }

  switch (selectedRepeat.value) {
    case 'WEEKLY':
      return [{ type: 'WEEKDAYS', value: dayjs(startDateTime.value).day().toString() }]
    case 'DAILY':
      return []
    default:
      return []
  }
})

const startDateTimeFilter = computed<TiggerFilter[]>(() => {
  if (!startDateTime.value) {
    return []
  }

  return [{ type: 'START_DATETIME', value: dayjs(startDateTime.value).format() }]
})

const endDateTimeFilter = computed<TiggerFilter[]>(() => {
  if (props.modelValue.hasEndDate && finalEndDateTime.value) {
    if (isFullDay.value && isRepeated.value && finalEndTime.value) {
      return [{ type: 'END_DATETIME', value: dayjs(finalEndTime.value).format() }]
    }

    if (!isFullDay.value && isRepeated.value && endTime.value) {
      return [
        {
          type: 'END_DATETIME',
          value: dayjs(finalEndDateTime.value)
            .set('hour', dayjs(endTime.value).hour())
            .set('minute', dayjs(endTime.value).minute())
            .set('second', dayjs(endTime.value).second())
            .set('millisecond', dayjs(endTime.value).millisecond())
            .format()
        }
      ]
    }

    return [{ type: 'END_DATETIME', value: dayjs(finalEndDateTime.value).endOf('day').format() }]
  }

  if (!isRepeated.value && endTime.value) {
    return [
      {
        type: 'END_DATETIME',
        value: dayjs(endTime.value).format()
      }
    ]
  }

  return []
})

const timeRangeFilter = computed<TiggerFilter[]>(() => {
  if (isRepeated.value && !isFullDay.value && startTime.value && endTime.value) {
    const startTimeFormatted = dayjs(startTime.value).format('HH:mm:ss.SSSZZ')
    const endTimeFormatted = dayjs(endTime.value).format('HH:mm:ss.SSSZZ')

    return [
      {
        type: 'TIME_RANGE',
        value: `${startTimeFormatted} - ${endTimeFormatted}`
      }
    ]
  }

  return []
})

const computedFilters = computed<TiggerFilter[]>(() => {
  return [...startDateTimeFilter.value, ...endDateTimeFilter.value, ...timeRangeFilter.value, ...repeatFilters.value]
})

const hasDateFilters = computed(() => {
  const dateFilters = props.modelValue.filters?.filter(
    (filter) => filter.type !== 'INCLUDED_SEGMENT' && filter.type !== 'EXCLUDED_SEGMENT'
  )

  return dateFilters?.length > 0
})

const setTimeRange = (date: Date) => {
  const startHours = startDateTime.value ? dayjs(startDateTime.value).hour() : 0
  const startMinutes = startDateTime.value ? dayjs(startDateTime.value).minute() : 0
  const endHours = endTime.value ? dayjs(endTime.value).hour() : 23
  const endMinutes = endTime.value ? dayjs(endTime.value).minute() : 59

  const newStartTime = dayjs(date).hour(startHours).minute(startMinutes).second(0).millisecond(0).toDate()
  startDateTime.value = newStartTime
  startTime.value = newStartTime // Initialiser startTime aussi

  const newEndTime = dayjs(date).hour(endHours).minute(endMinutes).second(59).millisecond(999).toDate()
  endTime.value = newEndTime
}

const handleAddSpecificDate = () => {
  setTimeRange(dayjs().toDate())
}

const handleUpdateDate = (date: Date) => {
  setTimeRange(date)
}

const handleToggleFullDay = () => {
  const date = startDateTime.value ? dayjs(startDateTime.value) : dayjs()
  const startOfDay = date.startOf('day').toDate()
  startDateTime.value = startOfDay
  startTime.value = startOfDay
  endTime.value = date.endOf('day').toDate()
}

const handleFullDayCheckboxChange = (isChecked: boolean) => {
  isFullDay.value = isChecked
  if (isChecked) {
    handleToggleFullDay()
  }
}

const handleUpdateStartDateTime = (value: Date) => {
  const baseDate = startDateTime.value ? dayjs(startDateTime.value) : dayjs()
  const valueDayjs = dayjs(value)
  const newDateTime = baseDate.hour(valueDayjs.hour()).minute(valueDayjs.minute()).second(0).millisecond(0).toDate()
  startDateTime.value = newDateTime
  startTime.value = newDateTime
}

const handleUpdateEndTime = (value: Date) => {
  const startDate = dayjs(startDateTime.value)
  const valueDayjs = dayjs(value)

  if (!props.modelValue.hasEndDate) {
    finalEndTime.value = startDate
      .hour(valueDayjs.hour())
      .minute(valueDayjs.minute())
      .second(59)
      .millisecond(999)
      .toDate()
  }

  endTime.value = startDate.hour(valueDayjs.hour()).minute(valueDayjs.minute()).second(59).millisecond(999).toDate()
}

const handleUpdateFinalEndDateTime = (value: Date) => {
  const endHours = finalEndTime.value ? dayjs(finalEndTime.value).hour() : 23
  const endMinutes = finalEndTime.value ? dayjs(finalEndTime.value).minute() : 59
  const newEndDateTime = dayjs(value).hour(endHours).minute(endMinutes).second(59).millisecond(999).toDate()
  finalEndDateTime.value = newEndDateTime
  finalEndTime.value = newEndDateTime
}

const handleUpdateFinalEndTime = (value: Date) => {
  if (finalEndDateTime.value) {
    const valueDayjs = dayjs(value)
    const newEndDateTime = dayjs(finalEndDateTime.value)
      .hour(valueDayjs.hour())
      .minute(valueDayjs.minute())
      .second(59)
      .millisecond(999)
      .toDate()
    finalEndDateTime.value = newEndDateTime
    finalEndTime.value = newEndDateTime
  }
}

const handleRepeatUpdate = (value: string) => {
  selectedRepeat.value = value
}

const handleAddRepeat = () => {
  isRepeated.value = true

  selectedRepeat.value = 'DAILY'

  if (!startTime.value && startDateTime.value) {
    startTime.value = dayjs(startDateTime.value).toDate()
  }
  if (!endTime.value && startDateTime.value) {
    endTime.value = dayjs(startDateTime.value).endOf('day').toDate()
  }

  emit('update:modelValue', {
    ...props.modelValue,
    hasEndDate: false
  })
}

const handleAddEndDate = () => {
  finalEndDateTime.value = null
  finalEndTime.value = endTime.value ? dayjs(endTime.value).toDate() : null

  emit('update:modelValue', {
    ...props.modelValue,
    hasEndDate: true
  })
}

const handleRemoveRepeat = () => {
  isRepeated.value = false
  selectedRepeat.value = null

  emit('update:modelValue', {
    ...props.modelValue,
    hasEndDate: false
  })
}

const handleRemoveEndDate = () => {
  finalEndDateTime.value = null
  finalEndTime.value = null

  emit('update:modelValue', {
    ...props.modelValue,
    hasEndDate: false
  })
}

const handleRemoveDateFilter = () => {
  isFullDay.value = false
  startDateTime.value = null
  startTime.value = null
  endTime.value = null

  isRepeated.value = false
  selectedRepeat.value = null

  finalEndDateTime.value = null
  finalEndTime.value = null

  emit('update:modelValue', {
    ...props.modelValue,
    filters: [],
    hasEndDate: false
  })
  emit('onChanges', { isCritical: true, field: 'filters', value: [] })
}

watch(computedFilters, () => {
  const stringNewFilters = JSON.stringify(computedFilters.value.sort((a, b) => a.type.localeCompare(b.type)))
  const stringModelFilters = JSON.stringify(
    [...(props.modelValue.filters ?? [])].sort((a, b) => a.type.localeCompare(b.type))
  )

  if (stringNewFilters !== stringModelFilters) {
    emit('update:modelValue', {
      ...props.modelValue,
      filters: computedFilters.value
    })
    emit('onChanges', { isCritical: true, field: 'filters', value: computedFilters.value })
  }
})

onMounted(() => {
  triggerType.value = props.modelValue.triggerType
  selectedTriggerType.value = props.modelValue.triggerType

  const startDateFilter = props.modelValue.filters?.find((filter) => filter.type === 'START_DATETIME')
  const endDateFilter = props.modelValue.filters?.find((filter) => filter.type === 'END_DATETIME')
  let timeRangeFilter = props.modelValue.filters?.find((filter) => filter.type === 'TIME_RANGE')
  const repeatWeekDaysFilter = props.modelValue.filters?.find((filter) => filter.type === 'WEEKDAYS')

  if (startDateFilter?.value) {
    startDateTime.value = dayjs(startDateFilter.value).toDate()
  }

  if (
    endDateFilter?.value &&
    startDateFilter?.value &&
    dayjs(startDateFilter.value).isSame(dayjs(endDateFilter.value), 'day')
  ) {
    const startTimePart = startDateFilter.value.split('T')[1]
    const endTimePart = endDateFilter.value.split('T')[1]

    if (startTimePart && endTimePart) {
      timeRangeFilter = {
        type: 'TIME_RANGE',
        value: `${startTimePart} - ${endTimePart}`
      }
    }
  }

  if (startDateTime.value) {
    const { start, end } = getTimeRangeDate(startDateTime.value, timeRangeFilter?.value)

    startTime.value = start
    endTime.value = end
  }

  isRepeated.value =
    repeatWeekDaysFilter?.value !== undefined ||
    (startDateFilter?.value !== undefined && endDateFilter?.value === undefined) ||
    (startDateFilter?.value !== undefined &&
      endDateFilter?.value !== undefined &&
      !dayjs(startDateFilter?.value).isSame(dayjs(endDateFilter?.value), 'day'))

  const isDaily = isRepeated.value && !repeatWeekDaysFilter?.value
  if (isDaily) selectedRepeat.value = 'DAILY'
  if (repeatWeekDaysFilter?.value) selectedRepeat.value = 'WEEKLY'

  finalEndDateTime.value = isRepeated.value && endDateFilter?.value ? dayjs(endDateFilter.value).toDate() : null
  finalEndTime.value = endDateFilter?.value ? dayjs(endDateFilter.value).toDate() : null

  if (startDateFilter?.value) {
    if (isRepeated.value && endDateFilter?.value && !timeRangeFilter?.value && !props.modelValue.hasEndDate) {
      const startMoment = dayjs(startDateFilter.value)
      const endMoment = dayjs(endDateFilter.value)
      isFullDay.value =
        startMoment.hour() === 0 && startMoment.minute() === 0 && endMoment.hour() === 23 && endMoment.minute() === 59
    } else if (startTime.value && endTime.value) {
      const startDayjs = dayjs(startTime.value)
      const endDayjs = dayjs(endTime.value)
      isFullDay.value =
        startDayjs.hour() === 0 && startDayjs.minute() === 0 && endDayjs.hour() === 23 && endDayjs.minute() === 59
    } else {
      isFullDay.value = false
    }
  } else {
    isFullDay.value = false
  }
})

// --- DateValue bridge helpers ---

const startCalendarDate = computed({
  get: () => (startDateTime.value ? parseDate(dayjs(startDateTime.value).format('YYYY-MM-DD')) : undefined),
  set: (v) => {
    const d = v ? v.toDate(getLocalTimeZone()) : dayjs().toDate()
    handleUpdateDate(d)
  }
})

const finalEndCalendarDate = computed({
  get: () => (finalEndDateTime.value ? parseDate(dayjs(finalEndDateTime.value).format('YYYY-MM-DD')) : undefined),
  set: (v) => {
    if (v) {
      handleUpdateFinalEndDateTime(v.toDate(getLocalTimeZone()))
    }
  }
})

const finalEndMinDate = computed(() =>
  startDateTime.value ? parseDate(dayjs(startDateTime.value).add(1, 'day').format('YYYY-MM-DD')) : undefined
)

// --- Time input helpers (HH:mm string <-> Date) ---

const startTimeInputValue = computed({
  get: () => (startDateTime.value ? dayjs(startDateTime.value).format('HH:mm') : ''),
  set: (v: string) => {
    if (!v) return
    const [h, m] = v.split(':').map(Number)
    const base = startDateTime.value ? dayjs(startDateTime.value) : dayjs()
    handleUpdateStartDateTime(base.hour(h).minute(m).second(0).millisecond(0).toDate())
  }
})

const endTimeInputValue = computed({
  get: () => (endTime.value ? dayjs(endTime.value).format('HH:mm') : ''),
  set: (v: string) => {
    if (!v) return
    const [h, m] = v.split(':').map(Number)
    const base = startDateTime.value ? dayjs(startDateTime.value) : dayjs()
    handleUpdateEndTime(base.hour(h).minute(m).second(59).millisecond(999).toDate())
  }
})

const finalEndTimeInputValue = computed({
  get: () => (finalEndTime.value ? dayjs(finalEndTime.value).format('HH:mm') : ''),
  set: (v: string) => {
    if (!v || !finalEndDateTime.value) return
    const [h, m] = v.split(':').map(Number)
    handleUpdateFinalEndTime(dayjs(finalEndDateTime.value).hour(h).minute(m).second(59).millisecond(999).toDate())
  }
})
</script>

<template>
  <div class="flex flex-col w-full">
    <div class="flex flex-row w-full">
      <div class="flex h-full flex-col items-center px-4">
        <div class="mt-[14px] flex min-h-7 w-7 items-center justify-center rounded-full border">
          <BaseIcon name="HeroCalendarIconOutline" class="h-4 w-4 text-foreground" />
        </div>
        <div v-if="hasDateFilters" class="flex h-full w-[1px] flex-1 border-l"></div>
      </div>

      <div class="flex flex-1 flex-col mb-4" style="width: calc(100% - 60px)">
        <div class="flex flex-col rounded-lg border bg-muted p-4">
          <div class="flex flex-col justify-stretch">
            <div class="flex flex-row justify-between items-center mb-3">
              <h3 class="text-sm font-medium text-foreground">
                {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.date') }}
              </h3>

              <BaseIcon
                v-if="hasDateFilters"
                name="HeroTrashIconOutline"
                class="h-5 w-5 cursor-pointer text-foreground-secondary hover:text-red-600"
                @click="handleRemoveDateFilter"
              />
            </div>

            <Button v-if="!hasDateFilters" variant="outline" class="w-full py-3" @click="handleAddSpecificDate">
              <Plus />
              {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.onSpecificDate') }}
            </Button>

            <div v-else class="flex flex-col w-full">
              <div class="flex flex-row items-center w-full gap-2">
                <!-- Date picker for startDateTime -->
                <Popover>
                  <PopoverTrigger as-child>
                    <Button
                      variant="outline"
                      class="flex-2 h-10 justify-start text-left font-normal text-sm"
                      :class="hasDateError ? 'border-red-600' : ''"
                    >
                      <CalendarIcon class="mr-2 size-4 shrink-0" />
                      {{
                        startDateTime
                          ? dayjs(startDateTime).format('DD/MM/YYYY')
                          : t('pages.marketingFlow.marketingFlowEditPage.trigger.selectDate')
                      }}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent class="z-[1200] w-auto p-0" align="start">
                    <Calendar v-model="startCalendarDate" />
                  </PopoverContent>
                </Popover>

                <!-- Time input for start time -->
                <div v-if="!isFullDay" class="flex-1 relative">
                  <div
                    class="flex items-center h-10 rounded-control border bg-background gap-2 text-sm"
                    :class="hasTimeRangeError ? 'border-red-600' : 'border-input'"
                  >
                    <input
                      type="time"
                      class="flex-1 bg-transparent border-0 outline-none text-sm"
                      :value="startTimeInputValue"
                      @change="
                        (e) => {
                          startTimeInputValue = (e.target as HTMLInputElement).value
                        }
                      "
                    />
                  </div>
                </div>

                <span v-if="!isFullDay">-</span>

                <!-- Time input for end time -->
                <div v-if="!isFullDay" class="flex-1 relative">
                  <div
                    class="flex items-center h-10 rounded-control border bg-background gap-2 text-sm"
                    :class="hasTimeRangeError ? 'border-red-600' : 'border-input'"
                  >
                    <input
                      type="time"
                      class="flex-1 bg-transparent border-0 outline-none text-sm"
                      :value="endTimeInputValue"
                      @change="
                        (e) => {
                          endTimeInputValue = (e.target as HTMLInputElement).value
                        }
                      "
                    />
                  </div>
                </div>
              </div>

              <AppCheckboxField
                id="isFullDay"
                v-model="isFullDay"
                :label="t('pages.marketingFlow.marketingFlowEditPage.trigger.isFullDay')"
                class="mt-3"
                @update:model-value="handleFullDayCheckboxChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="hasDateFilters && !isRepeated">
      <div
        class="mb-4 flex cursor-pointer flex-row px-5 py-2 text-primary hover:text-primary/80"
        @click="handleAddRepeat"
      >
        <BaseIcon name="HeroPlusIconOutline" class="mr-2.5 h-5 w-5" />
        <span class="text-sm font-medium">{{ t('pages.marketingFlow.marketingFlowEditPage.trigger.addRepeat') }}</span>
      </div>
    </div>

    <div v-if="hasDateFilters && isRepeated" class="flex flex-row w-full">
      <div class="flex h-full flex-col items-center px-4">
        <div class="flex min-h-[14px] w-[1px] border-l"></div>

        <div class="flex min-h-7 w-7 items-center justify-center rounded-full border">
          <BaseIcon name="HeroArrowPathRoundedSquareIconSolid" class="h-4 w-4 text-foreground" />
        </div>
        <div class="flex h-full w-[1px] flex-1 border-l"></div>
      </div>

      <div class="flex flex-1 flex-col mb-4" style="width: calc(100% - 60px)">
        <div class="rounded-lg border bg-muted p-4">
          <div class="flex flex-row items-center mb-3">
            <div class="flex flex-row items-center flex-1">
              {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.repeat') }}
            </div>

            <BaseIcon
              v-if="hasDateFilters"
              name="HeroTrashIconOutline"
              class="h-5 w-5 cursor-pointer text-foreground-secondary hover:text-red-600"
              @click="handleRemoveRepeat"
            />
          </div>

          <Select :model-value="selectedRepeat" @update:model-value="handleRepeatUpdate">
            <SelectTrigger class="h-10 w-full">
              <SelectValue
                :placeholder="t('pages.marketingFlow.marketingFlowEditPage.trigger.repeat_choice.placeholder')"
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="option in repeatOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>

    <div v-if="hasDateFilters && isRepeated && !modelValue.hasEndDate">
      <div
        class="mb-4 flex cursor-pointer flex-row px-5 py-2 text-primary hover:text-primary/80"
        @click="handleAddEndDate"
      >
        <BaseIcon name="HeroPlusIconOutline" class="mr-2.5 h-5 w-5" />
        <span class="text-sm font-medium">{{ t('pages.marketingFlow.marketingFlowEditPage.trigger.addEndDate') }}</span>
      </div>
    </div>

    <div v-if="hasDateFilters && modelValue.hasEndDate" class="flex flex-row w-full">
      <div class="flex h-full flex-col items-center px-4">
        <div class="flex min-h-[14px] w-[1px] border-l"></div>

        <div class="flex min-h-7 w-7 items-center justify-center rounded-full border">
          <BaseIcon name="HeroPauseIconSolid" class="h-4 w-4 text-foreground" />
        </div>
      </div>

      <div class="flex flex-1 flex-col mb-4" style="width: calc(100% - 60px)">
        <div class="flex flex-col rounded-lg border bg-muted p-4">
          <div class="flex flex-col justify-stretch">
            <div class="flex flex-row items-center mb-3">
              <div class="flex flex-row items-center flex-1">
                {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.endDate') }}
              </div>

              <BaseIcon
                v-if="hasDateFilters"
                name="HeroTrashIconOutline"
                class="h-5 w-5 cursor-pointer text-foreground-secondary hover:text-red-600"
                @click="handleRemoveEndDate"
              />
            </div>

            <div class="flex flex-row items-center w-full gap-2">
              <!-- Date picker for finalEndDateTime -->
              <Popover>
                <PopoverTrigger as-child>
                  <Button
                    variant="outline"
                    class="flex-1 h-10 justify-start text-left font-normal text-sm"
                    :class="hasDateError ? 'border-red-600' : ''"
                  >
                    <CalendarIcon class="mr-2 size-4 shrink-0" />
                    {{
                      finalEndDateTime
                        ? dayjs(finalEndDateTime).format('DD/MM/YYYY')
                        : t('pages.marketingFlow.marketingFlowEditPage.trigger.selectDate')
                    }}
                  </Button>
                </PopoverTrigger>
                <PopoverContent class="z-[1200] w-auto p-0" align="start">
                  <Calendar v-model="finalEndCalendarDate" :min-value="finalEndMinDate" />
                </PopoverContent>
              </Popover>

              <!-- Time input for finalEndTime (shown when isFullDay) -->
              <div v-if="isFullDay" class="flex-1 relative">
                <div
                  class="flex items-center h-10 rounded-control border bg-background px-3 gap-2 text-sm"
                  :class="[
                    hasTimeRangeError ? 'border-red-600' : 'border-input',
                    !finalEndDateTime ? 'opacity-50 pointer-events-none' : ''
                  ]"
                >
                  <BaseIcon name="HeroClockIconOutline" class="h-4 w-4 text-muted-foreground shrink-0" />
                  <input
                    type="time"
                    class="flex-1 bg-transparent outline-none text-sm"
                    :disabled="!finalEndDateTime"
                    :value="finalEndTimeInputValue"
                    @change="
                      (e) => {
                        finalEndTimeInputValue = (e.target as HTMLInputElement).value
                      }
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
